import { V3dTools } from '@/v3d/common';
import { Part, partProps } from '@/Engine/Wardrobe/Part';
import {
    invalidPlaceForShelf,
    leftArrowName,
    lineModelName,
    rackCollisionDistance,
    rackHoles,
    rackShift,
    unreachablePoint,
} from '@/Constants/common';
import { iPartTemplate } from '@/Interfaces/partTemplate';
import { IV3D } from '@/Interfaces/v3d';
import { align, SceneObjects, Shelf, sides, wardrobeItem, wardrobeModule, xyz } from '@/Interfaces/common';
import { WardrobeObject } from '@/Engine/Wardrobe/WardrobeObject';
import { Box3, Group, Quaternion, Vector3 } from 'three';
import { Wardrobe } from '@/Engine/Wardrobe/Wardrobe';
import { findRack, getConnectorRack, getRackWithMaxHeight } from '@/lib/createModule';
import { useConfigurationStore } from '@/Store/storeConfiguration';
import { $ } from '@/Engine/state';
import { RailGroup } from '@/Engine/Wardrobe/RailGroup';
import { addBoxHelpers } from '@/Engine/addBoxHelper';
import { shelvesBracketHeight } from '@/Constants/shelves';
import { PerforatedPanel } from '@/Engine/Wardrobe/PerforatedPanel';
import { TextMesh } from '@/Engine/Ruler/TextMesh';
import { _checkAllCollision } from '@/Engine/checkCollision';

declare let v3d: IV3D;
interface rackModuleProps extends wardrobeModule {
    // onChangePosition: (uuid: string, position: xyz)=>void;
    v3dTools: V3dTools;
    parent: Wardrobe;
}
export class RackModule extends WardrobeObject {
    rackLeft: Part | null = null;
    rackRight: Part | null = null;
    rackLeft2: Part | null = null;
    rackRight2: Part | null = null;
    connectorLeft: Part | null = null;
    connectorRight: Part | null = null;
    // rackMiddle: Part | null = null;
    v3dTools: V3dTools;
    railAlign: align;
    parent: Wardrobe;
    templates: wardrobeItem[];
    side: sides;
    stacked = false;
    railGroup: RailGroup | null = null;
    rulerGroup: Group = new Group();

    constructor({ uuid, templates, width, height,
                    depth, railAlign = 'center', parent, v3dTools, color, side }: rackModuleProps) {
        super({
            compatibilityDepth: '', compatibilityWidth: '', side, uuid, width, height, depth,
            raycastBox: true, type: 'rackModule', color });
        if (this.width === 0.6) {
            this.width = this.width + 0.007;
        }

        if (this.width === 0.9) {
            this.width = this.width + 0.001;
        }

        this.v3dTools = v3dTools;
        this.railAlign = railAlign;
        this.parent = parent;
        this.templates = JSON.parse(JSON.stringify(templates));
        this.side = side;
        this.model.userData.side = side;
        this.model.add(this.rulerGroup);
        this.updateModels(this.templates);
        this.addRuler();
        this.updatePartPosition();
        // this.raycastBox.updateGeometry(this.width, this.height, this.depth);
        this._updateBox();
    }

    get leftEnable() {
        return !this.templates[0]?.disabled;
    }

    get rightEnable() {
        return !this.templates[1]?.disabled;
    }

    // Вернет true если на рейке юолше одного модуля
    get onRailGroup() {
        return this.railGroup && this.railGroup.racks.length > 1;
    }

    get stackedModules() {
        return this.railGroup && this.railGroup.racks;
    }

    get raw(): wardrobeModule {
        return JSON.parse(JSON.stringify(
            {
                uuid: this.uuid,
                templates: this.templates,
                width: this.width,
                //TODO напоминалка- высота = высота rack+rack2 (для того чтоб полка move на rack2)
                height: this.height,
                depth: this.depth,
                position: this.position.toArray() as xyz,
                railAlign: this.railAlign,
                color: this.color,
                // children: this.childrenData,
                side: this.side,
                railGroupUUID: this.railGroup?.uuid,
            },
        ));
    }

    setPositionWithShelves(position: Vector3) {
        this.position = position;
        this.shelves.forEach((shelf)=> {
            shelf.position = position;
        });
        // this.parent.moveShelvesByModule(this.uuid, position);
        if (this.railGroup)
            this.railGroup.updatePosition();
    }

    get shelves(): Array<Shelf> {
        return this.parent?.getShelvesByModuleUUID(this.uuid);
    }

    set position(position: Vector3) {
        if (this.railGroup && this.railGroup.racks.length < 2) {
            this.railGroup.position = position;
            if (this.railGroup.uuid !== 'temp' || $.tempRail)
                $.room.addToWall(this.railGroup.model, this.side);
        }
        super.position = position;
        // this.railModel.position.copy(this.model.position);
        this.computeObb();
        // TODO надо проверять есть ли наложения стоек,
        // если есть, то нужно выбрать и оставить наиболее длинную стойку
        this.checkRackCollision();

        this.railGroup && this.railGroup.update();
    }

    checkRackCollision() {
        if (!this.railGroup) return;
        // if (this.railGroup.racks.length < 2) return;

        for (const rack of this.railGroup.racks) {
            if (rack.templates[0].disabled || rack.templates[1].disabled) {
                rack.templates[0].disabled = false;
                rack.templates[1].disabled = false;
                if (rack.templates[2]) rack.templates[2].disabled = false;
                if (rack.templates[3]) rack.templates[3].disabled = false;
                if (rack.templates[4]) rack.templates[4].disabled = false;
                if (rack.templates[5]) rack.templates[5].disabled = false;
                rack.update();
            }

        }

        for (const rack1 of this.railGroup.racks) {
            for (const rack2 of this.railGroup.racks) {
                if (rack1.uuid === rack2.uuid) continue;
                if (
                    ((rack2.rightEnable && rack1.upLeftPoint.distanceTo(rack2.upRightPoint) < rackCollisionDistance) ||
                        (rack2.leftEnable && rack1.upLeftPoint.distanceTo(rack2.upLeftPoint) < rackCollisionDistance)) &&
                    rack2.height >= rack1.height) {
                    rack1.templates[0].disabled = true;
                    if (rack1.templates[2]) {
                        rack1.templates[2].disabled = true;
                        rack1.templates[4].disabled = true;
                    }
                    rack1.update();
                    // needUpdate = true;
                    // rack2.checkRackCollision();
                }

                if (((rack2.rightEnable && rack1.upRightPoint.distanceTo(rack2.upRightPoint) < rackCollisionDistance) ||
                        (rack2.leftEnable && rack1.upRightPoint.distanceTo(rack2.upLeftPoint) < rackCollisionDistance)) &&
                    rack2.height >= rack1.height) {
                    rack1.templates[1].disabled = true;
                    if (rack1.templates[3]) {
                        rack1.templates[3].disabled = true;
                        rack1.templates[5].disabled = true;
                    }
                    rack1.update();

                }
            }
        }

    }

    get position() {
        return super.position;
    }

    get nearestLeftModuleDistance() {
        if (this.railGroup && this.railGroup.racks.length < 2) return null;

        let minDistance: number | null = null;
        this.railGroup?.racks.forEach((rack)=>{
            if (this.uuid === rack.uuid) return;
            if (rack.upLeftPoint.x < this.upLeftPoint.x &&
                 this.upLeftPoint.x < rack.upRightPoint.x) {
                minDistance = 0;
            }
            else
                if (rack.upRightPoint.x > this.upRightPoint.x) return;

            const distance = rack.upRightPoint.distanceTo(this.upLeftPoint);
            (minDistance === null ||
                minDistance > distance) &&
                    (minDistance = distance);
        });
        return minDistance;
    }

    get nearestRightModuleDistance() {
        if (!this.railGroup) return null;
        if (this.railGroup.racks.length < 2) return null;
        let minDistance: number | null = null;
        this.railGroup.racks.forEach((rack)=>{
            if (this.uuid === rack.uuid) return;
            if (rack.upLeftPoint.x < this.upRightPoint.x &&
                this.upRightPoint.x < rack.upRightPoint.x) {
                minDistance = 0;
            }
            else
                if (rack.upLeftPoint.x < this.upLeftPoint.x) return;
            const distance = rack.upLeftPoint.distanceTo(this.upRightPoint);
            (minDistance === null ||
            minDistance > distance) &&
            (minDistance = distance);
        });
        return minDistance;
    }

    get leftPoint() {
        return this.position.clone().add(new Vector3(-this.localSize.x/2,0,0));
    }

    get upLeftPoint(): Vector3 {
        const upLeft = this.leftPoint;
        upLeft.y = this.position.y;
        return upLeft;
    }

    get rightPoint() {
        // const sizes = new Vector3();
        // this.box.getSize(sizes);
        return this.position.clone().add(new Vector3(this.localSize.x/2,0,0));
    }

    get upRightPoint(): Vector3 {
        const upRight = this.rightPoint;
        upRight.y = this.position.y;
        // upRight.z = 0;
        return upRight;
    }

    checkAvailableUnits() {
        //TODO
        return true;
    }

    getRaycastBoxes() {
        const boxes = [];
        // boxes.push(...this.getRaycastChildrenBoxes());
        boxes.push(this.raycastBox.model);
        return boxes.filter((box)=>box.parent);
    }

    setColor(hex: string) {
        const color = hex.startsWith('#')?hex:'#'+hex;
        this.color = color.replace('#','');
        this.rackLeft?.setColor(color);
        this.rackRight?.setColor(color);
        this.rackLeft2?.setColor(color);
        this.rackRight2?.setColor(color);
        this.connectorLeft?.setColor(color);
        this.connectorRight?.setColor(color);
        // this.rackMiddle?.setColor(color);
        this.templates.forEach((rack)=>rack.color = color.replace('#',''));
    }

    updatePartPosition() {
        if (this.rackLeft) {
            this.rackLeft.model.rotation.y = Math.PI;
            this.rackLeft.model.position.x = - this.width / 2;
            this.rackLeft.model.position.y = 0.01;
            this.rackLeft.model.position.z = 0.002;
        }
        if (this.rackLeft && this.rackLeft2) {
            this.rackLeft2.model.rotation.y = Math.PI;
            this.rackLeft2.model.position.x = this.rackLeft.model.position.x;
            this.rackLeft2.model.position.y = -this.rackLeft.height + this.rackLeft.model.position.y - 0.008;
            this.rackLeft2.model.position.z = this.rackLeft.model.position.z;
        }

        if (this.rackRight) {
            this.rackRight.model.rotation.y = Math.PI;
            this.rackRight.model.position.x = this.width / 2;
            this.rackRight.model.position.y = 0.01;
            this.rackRight.model.position.z = 0.002;
        }

        if (this.rackRight && this.rackRight2) {
            this.rackRight2.model.rotation.y = Math.PI;
            this.rackRight2.model.position.x = this.rackRight.model.position.x;
            this.rackRight2.model.position.y = -this.rackRight.height + this.rackRight.model.position.y - 0.008;
            this.rackRight2.model.position.z = this.rackRight.model.position.z;
        }

        if (this.connectorLeft && this.rackLeft2) {
            this.connectorLeft.model.position.x = this.rackLeft2.model.position.x;
            this.connectorLeft.model.position.y = this.rackLeft2.model.position.y + 0.002;
            this.connectorLeft.model.position.z = this.rackLeft2.model.position.z + 0.025;
        }
        if (this.connectorRight && this.rackRight2) {
            this.connectorRight.model.position.x = this.rackRight2.model.position.x;
            this.connectorRight.model.position.y = this.rackRight2.model.position.y + 0.002;
            this.connectorRight.model.position.z = this.rackRight2.model.position.z + 0.025;
        }
        // if (this.rackMiddle) {
        //     this.rackMiddle.model.rotation.y = Math.PI;
        //     this.rackMiddle.model.position.x = 0;
        //     this.rackMiddle.model.position.y = 0.01;
        //     this.rackMiddle.model.position.z = 0.002;
        // }

        this.startPoint = [
            - this.width / 2,
            - this.height,
            0,
        ];
        this._pointer.position.set(...this.startPoint);
        // const globalPosition = new Vector3();
        // this._pointer.getWorldPosition(globalPosition);
        // this.startPointGlobal = [
        //     globalPosition.x,// - this.width / 2,
        //     globalPosition.y,//- this.height,
        //     globalPosition.z,
        // ];
    }

    // getRightStackPosition() {
    //     return new Vector3(
    //         this.model.position.x+this.width/2 + 2*rackShift,
    //         this.model.position.y,
    //         this.model.position.z,
    //     );
    // }
    //
    // getLeftStackPosition() {
    //     return new Vector3(
    //         this.model.position.x -this.width/2 - 2*rackShift,
    //         this.model.position.y,
    //         this.model.position.z,
    //     );
    // }

    update() {
        this.updateModels(this.templates);
        this.updatePartPosition();
    }

    createRackOrConnector = (startPart, enable, obj, template) => {
        if (enable && obj && template) {
            if (startPart) {
                startPart.destroy();
            }
            startPart = new Part(<partProps>{
                ...template,
                model: obj,
                uuid: template.uuid ?? crypto.randomUUID(),
                parent: this,
                side: this.side,
            });
            startPart.model.position.add(new Vector3(0,0,0.01));
            this.model.add(startPart.model);
        } else {
            if (startPart)
                startPart.destroy();
            startPart = null;
        }

        return startPart;

    };

    updateModels(templates: wardrobeItem[]) {
        let leftRackObj;
        let rightRackObj;
        let leftRack2Obj;
        let rightRack2Obj;
        let leftConnectorObj;
        let rightConnectorObj;
        // let middleRackObj;
        let leftRackTemplate: wardrobeItem | null = null;
        let rightRackTemplate: wardrobeItem | null = null;
        let leftRack2Template: wardrobeItem | null = null;
        let rightRack2Template: wardrobeItem | null = null;
        let leftConnectorTemplate: wardrobeItem | null = null;
        let rightConnectorTemplate: wardrobeItem | null = null;
        // const middleRackTemplate: wardrobeItem | null = null;
        const rackNum = templates.length;
        if (rackNum === 2) {
            leftRackTemplate = templates[0].id?templates[0]:null;
            rightRackTemplate = templates[1].id?templates[1]:null;
        }
        if (rackNum === 6) {
            leftRackTemplate = templates[0].id?templates[0]:null;
            rightRackTemplate = templates[1].id?templates[1]:null;
            leftRack2Template = templates[2].id?templates[2]:null;
            rightRack2Template = templates[3].id?templates[3]:null;
            leftConnectorTemplate = templates[4].id?templates[4]:null;
            rightConnectorTemplate = templates[5].id?templates[5]:null;
        }
        // if (rackNum === 3) {
        //     leftRackTemplate = templates[0].id?templates[0]:null;
        //     middleRackTemplate = templates[1].id?templates[1]:null;
        //     rightRackTemplate = templates[2].id?templates[2]:null;
        // }
        // console.log(middleRackTemplate);
        if (leftRackTemplate) leftRackObj = this.v3dTools.getObjectByName(leftRackTemplate.name);
        if (rightRackTemplate) rightRackObj = this.v3dTools.getObjectByName(rightRackTemplate.name);
        if (leftRack2Template) leftRack2Obj = this.v3dTools.getObjectByName(leftRack2Template.name);
        if (rightRack2Template) rightRack2Obj = this.v3dTools.getObjectByName(rightRack2Template.name);
        if (leftConnectorTemplate) leftConnectorObj = this.v3dTools.getObjectByName(leftConnectorTemplate.name);
        if (rightConnectorTemplate) rightConnectorObj = this.v3dTools.getObjectByName(rightConnectorTemplate.name);
        // if (middleRackTemplate) middleRackObj = this.v3dTools.getObjectByName(middleRackTemplate.name);

        this.rackLeft = this.createRackOrConnector(this.rackLeft, this.leftEnable, leftRackObj, leftRackTemplate);
        this.rackRight = this.createRackOrConnector(this.rackRight, this.rightEnable, rightRackObj, rightRackTemplate);
        this.rackLeft2 = this.createRackOrConnector(this.rackLeft2, this.leftEnable, leftRack2Obj, leftRack2Template);
        this.rackRight2 = this.createRackOrConnector(this.rackRight2, this.rightEnable, rightRack2Obj, rightRack2Template);

        this.connectorLeft = this.createRackOrConnector(this.connectorLeft, this.leftEnable, leftConnectorObj, leftConnectorTemplate);
        this.connectorRight = this.createRackOrConnector(this.connectorRight, this.rightEnable, rightConnectorObj, rightConnectorTemplate);


        // if (middleRackObj && middleRackTemplate) { //&& !this.moduleRight TODO
        //     this.rackMiddle = new Part(<partProps>{
        //         ...middleRackTemplate,
        //         model: middleRackObj,
        //         uuid: middleRackTemplate.uuid ?? crypto.randomUUID(),
        //         side: this.side,
        //         parent: this,
        //     });
        //     this.rackMiddle.model.position.add(new Vector3(0,0,0.01));
        //     this.model.add(this.rackMiddle.model);
        // } else {
        //     if (this.rackMiddle)
        //         this.model.remove(this.rackMiddle.model);
        //     this.rackMiddle = null;
        // }

        this.model.userData.type = 'rack';
        this.model.userData.uuid = this.uuid;
    }

    checkConnectorCollision() {
        let collisionLeft = [], collisionRight = [];
        //Определяем коллизию
        //1 для полок без кронштейнов по obb полки
        //2 для полок с кронштейнам по допустимым place кронштейнов
        const shelvesWithoutBracket: Shelf[] = [];
        const shelvesWithBracket: Shelf[] = [];
        [ ...$.wardrobe.shelves ].forEach(x => {
            if (x.items?.shelfBracketLeft === undefined) {
                shelvesWithoutBracket.push(x);
            } else {
                shelvesWithBracket.push(x);
            }
        });

        if (this.connectorLeft) {
            collisionLeft = _checkAllCollision(this.connectorLeft as SceneObjects, shelvesWithoutBracket);
            shelvesWithBracket.forEach(x => {
                //полка на месте соединителя стоек + выделяем соединитель только относящийся к текущей полке
                if (invalidPlaceForShelf.includes(x.place) && x.rackModule?.connectorLeft === this.connectorLeft) {
                    collisionLeft.push(x);
                }
            });
        }
        //выделяю коллизии парой по аналогии с кронштейнами
        if (collisionLeft?.length) {
            this.connectorRight?.redMark();
            this.connectorLeft?.redMark();
        } else {
            this.connectorRight?.noMark();
            this.connectorLeft?.noMark();
        }
    }
    checkRackCollisions() {
        this.rackLeft?.noMark();
        this.rackRight?.noMark();
        this.rackLeft2?.noMark();
        this.rackRight2?.noMark();
        this.connectorRight?.noMark();
        this.connectorLeft?.noMark();
        // Рейка не должна пересекать панель
        const rightCollisions = this.rackRightCollision();
        const error = 0.01;

        this.checkConnectorCollision();
        if (Array.isArray(rightCollisions))
            rightCollisions?.forEach((rightCollision)=> {
                if (rightCollision) {
                    if (rightCollision.type === 'box') {
                        this.rackRight?.redMark();
                        this.rackRight2?.redMark();
                        this.connectorRight?.redMark();
                    }
                    if ([ 'window', 'door' ].includes(rightCollision.type)) {
                        this.rackRight?.redMark();
                        this.rackRight2?.redMark();
                        this.connectorRight?.redMark();
                    }
                    if (rightCollision.type === 'panel' &&
                        this.position.distanceTo(rightCollision.position) > 0.08 &&
                        Math.abs(this.position.x - rightCollision.position.x) < this.width / 2 + rightCollision.width / 2 - error
                    ) {
                        const shelf = rightCollision as PerforatedPanel;
                        shelf._model?.redMark();
                        this.rackRight?.redMark();
                        this.rackRight2?.redMark();
                        this.connectorRight?.redMark();
                    }
                }
            });

        const leftCollisions = this.rackLeftCollision();
        if (Array.isArray(leftCollisions))
            leftCollisions?.forEach((leftCollision)=> {
                if (leftCollision) {
                    if (leftCollision.type === 'box') {
                        this.rackLeft?.redMark();
                        this.rackLeft2?.redMark();
                        this.connectorLeft?.redMark();
                    }
                    if ([ 'window', 'door' ].includes(leftCollision.type)) {
                        this.rackLeft?.redMark();
                        this.rackLeft2?.redMark();
                        this.connectorLeft?.redMark();
                    }

                    if (leftCollision.type === 'panel' &&
                        this.position.distanceTo(leftCollision.position) > 0.08 &&
                        Math.abs(this.position.x - leftCollision.position.x) < this.width / 2 + leftCollision.width / 2 - error
                    ) {
                        const shelf = leftCollision as PerforatedPanel;
                        shelf._model?.redMark();
                        this.rackLeft?.redMark();
                        this.rackLeft2?.redMark();
                        this.connectorLeft?.redMark();
                    }
                }
            });
    }

    rackRightCollision() {
        if (!this.rackRight) return false;
        this.rackRight.computeObb(new Vector3(0,-this.rackRight.height/2,this.rackRight.depth/2));
        this.rackRight2?.computeObb(new Vector3(0,-this.rackRight2?.height/2,this.rackRight2?.depth/2));
        this.connectorRight?.computeObb(new Vector3(0,0,this.connectorRight?.depth));

        const shelves = this.shelves.map((shelf)=>shelf.uuid);
        const collisions = $.checkAllShelfCollisionWithExclude(
            this.rackRight,
            this.side,
            shelves,
        );
        let collisions2 = [], collisionsConnector = [];
        if (this.rackRight2) {
            collisions2 = $.checkAllShelfCollisionWithExclude(
                this.rackRight2,
                this.side,
                shelves,
            );
        }
        if (this.connectorRight) {
            collisionsConnector = $.checkAllShelfCollisionWithExclude(
                this.connectorRight,
                this.side,
                shelves,
            );
        }

        return [ ...collisions, ...collisions2, ...collisionsConnector ];
    }

    rackLeftCollision() {
        if (!this.rackLeft) return false;
        this.rackLeft.computeObb(new Vector3(0,-this.rackLeft.height/2,this.rackLeft.depth/2));
        this.rackLeft2?.computeObb(new Vector3(0,-this.rackLeft2?.height/2,this.rackLeft2?.depth/2));
        this.connectorLeft?.computeObb(new Vector3(0,0,this.connectorLeft?.depth));

        const shelves = this.shelves.map((shelf)=>shelf.uuid);
        const collisions = $.checkAllShelfCollisionWithExclude(
            this.rackLeft,
            this.side,
            shelves,
        );
        let collisions2 = [], collisionsConnector = [];
        if (this.rackLeft2) {
            collisions2 = $.checkAllShelfCollisionWithExclude(
                this.rackLeft2,
                this.side,
                shelves,
            );
        }
        if (this.connectorLeft) {
            collisionsConnector = $.checkAllShelfCollisionWithExclude(
                this.connectorLeft,
                this.side,
                shelves,
            );
        }
        return [ ...collisions, ...collisions2, ...collisionsConnector ];
    }

    checkRackHeight(place: number, shelfHeight:number) {
        const height = (place+1)*rackHoles + shelfHeight;
        return height <= this.height;
    }

    getCurrentHeight(shelfHeight:number) {
        const items=$.wardrobe.shelves.filter((item)=> item.rackModule && item.rackModule.uuid === this.uuid);
        const maxPlace = Math.max(...items.map((item)=>item.place));
        const height = (maxPlace+1)*rackHoles + shelfHeight;
        return height;
    }
    getNewRack2(shelfHeight:number) {
        const hex = useConfigurationStore().getWardrobeBaseColor.replace('#','');
        const heightCurrent = this.getCurrentHeight(shelfHeight);
        const height = this.rackLeft?.height ?? this.rackRight?.height ?? heightCurrent;
        return findRack(heightCurrent - height, hex);
    }
    getNewRack(shelfHeight:number) {
        const height = this.getCurrentHeight(shelfHeight);
        return findRack(height, useConfigurationStore().getWardrobeBaseColor.replace('#',''));
    }

    getMaxPlace(shelfHeight:number){
        return Math.ceil(this.height/rackHoles-(shelfHeight/rackHoles)-1) ;
    }

    updateRacks() {
        if (!this.shelves || !this.shelves.length) return;
        const lowShelf = this.shelves.reduce((prev, current) => {
            return (prev._place > current._place) ? prev : current;
        });
        let shelfHeight= 0;
        lowShelf.type === 'shelf'? shelfHeight=shelvesBracketHeight : shelfHeight=lowShelf.height;
        const newRack = this.getNewRack(shelfHeight);
        if (!newRack) return null;
        this.height = newRack.height;
        const oldRackId = this.raw.templates[0].id || this.raw.templates[1].id;
        if (!oldRackId) return null;
        const oldRack2Id = this.raw.templates[2]?.id || this.raw.templates[3]?.id;
        const newModule = this.raw;
        let newRack2, connector;
        newModule.templates = [];
        const disableLeft: boolean|undefined = this.raw.templates[0]?.disabled;
        const disableRight: boolean|undefined = this.raw.templates[1]?.disabled;
        newModule.templates[0] = newRack;
        newModule.templates[1] = newRack;
        newModule.templates[0].disabled = disableLeft;
        newModule.templates[1].disabled = disableRight;

        if ($.addConnector === true) {
            newRack2 = this.getNewRack2(shelfHeight);
            this.height = newRack.height + (newRack2?.height ?? 0);
            connector = getConnectorRack();
            newModule.templates[2] = newRack2;
            newModule.templates[3] = newRack2;
            newModule.templates[4] = connector;
            newModule.templates[5] = connector;
            newModule.templates[2].disabled = disableLeft;
            newModule.templates[3].disabled = disableRight;
            newModule.templates[4].disabled = disableLeft;
            newModule.templates[5].disabled = disableRight;
        }


        if (oldRackId !== newRack.id || (newRack2 && oldRack2Id !== newRack2.id)) {
            this.templates = JSON.parse(JSON.stringify(newModule.templates));
            this.height = newRack.height + (newRack2?.height ?? 0);
            this.updateModels(this.templates);
            this.updatePartPosition();
            this.setColor(newRack.color);
            this._updateBox();
        }
    }

    _updateBox() {
        this.box.makeEmpty();
        const shelfBox = new Box3();
        this.shelves?.forEach((shelf) => {
            shelfBox.expandByPoint(shelf.box.min);
            shelfBox.expandByPoint(shelf.box.max);
        });
        const shelfBoxSize = new Vector3();
        shelfBox.getSize(shelfBoxSize);
        this.raycastBox.updateGeometry(this.width, this.height + 0.022, shelfBoxSize.z ?? this.depth);
        if (this.rayCastBoxEnable) {
            this.box.expandByObject(this.raycastBox.model);
        } else {
            // this.decorModel.removeFromParent();
            this.box.expandByObject(this.model);
            // this.model.add(this.decorModel);
        }
        this.box.expandByVector(new Vector3(0.0125,0,0));
    }

    addRuler = () => {
        this.rulerGroup.clear();
        if (!$.isRuler) return;
        this.rulerGroup.position.set(0,0.06, 0.02);
        const text = '' + (this.width * 1000);
        const textMesh = new TextMesh(text);
        const lineCenter:Vector3 = new Vector3();
        this.rulerGroup.add(textMesh.model);
        const modelLineProto = $.v3dTools.getObjectByName(lineModelName);
        if (!modelLineProto) {
            console.warn('No model find: ', lineModelName);
            return;
        }

        const modelArrowProto = $.v3dTools.getObjectByName(leftArrowName);
        if (!modelArrowProto) {
            console.warn('No model find: ', lineModelName);
            return;
        }

        // const arrowModel = modelArrowProto.clone();
        // arrowModel.material.depthWrite = false;
        // arrowModel.material.depthTest = false;
        // arrowModel.renderOrder = 70;
        // arrowModel.rotation.x = Math.PI/2;
        // arrowModel.position.set(-0.045, -0.043, 0);
        // arrowModel.scale.set(1, 1, 1);
        // this.rulerGroup.add(arrowModel);
        //
        // const lineModelHeight = modelLineProto.clone();
        // lineModelHeight.material.depthWrite = false;
        // lineModelHeight.material.depthTest = false;
        // lineModelHeight.renderOrder = 70;
        // lineModelHeight.rotation.x = Math.PI/2;
        // lineModelHeight.rotation.y = Math.PI/2;
        // lineModelHeight.position.set(0, -0.032, 0);
        // lineModelHeight.scale.set(1, 1, 6);
        // this.rulerGroup.add(lineModelHeight);
    };

    // addShelf(newShelf: Shelf) {
    //     newShelf._position = this._position;
    //     newShelf.rackModule = this;
    //
    // }

    computeObb() {
        this.worldBox = this.box.clone();
        const worldQuaternion = new Quaternion();
        this.model.getWorldQuaternion(worldQuaternion);
        const sizes = new Vector3();
        this.box.getSize(sizes);
        sizes.applyQuaternion(worldQuaternion);
        const shift = new Vector3(0,-Math.abs(sizes.y/2) + 0.022, Math.abs(sizes.z/2)).applyQuaternion(worldQuaternion);
        super.computeObb(shift, 0);

    }

    destroy() {
        this.rackLeft?.destroy();
        this.rackRight?.destroy();
        this.rackLeft2?.destroy();
        this.rackRight2?.destroy();
        this.connectorLeft?.destroy();
        this.connectorRight?.destroy();
        // this.rackMiddle?.destroy();
        this.model.clear();
        this.model.removeFromParent();
    }
}
